import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSellerFinancials, getFinancialSummary } from '../lib/financialService';
import { COLORS, TYPOGRAPHY } from '../lib/styleGuide';
import { ArrowLeft, TrendingUp, TrendingDown, Wallet, Document, TimeCircle, TickSquare, Show, Notification } from 'react-iconly';
import { SellerCancellationDashboard } from '../components/SellerCancellationDashboard';

const { headingFont, bodyFont } = TYPOGRAPHY;

export default function SellerFinancials() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  console.log('SellerFinancials component rendered - currentUser:', currentUser);
  
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  useEffect(() => {
    console.log('useEffect triggered - currentUser:', currentUser, 'currentUser.uid:', currentUser?.uid, 'dateRange:', dateRange);
    if (currentUser?.uid) {
      console.log('User has uid, calling fetchFinancials');
      fetchFinancials();
    } else {
      console.log('User does not have uid, skipping fetchFinancials');
    }
  }, [dateRange, currentUser?.uid]);

  const fetchFinancials = async () => {
    if (!currentUser?.uid) {
      console.log('User not loaded yet, skipping financials fetch');
      return;
    }
    
    try {
      console.log('Starting to fetch financials for user:', currentUser.uid);
      setLoading(true);
      
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const dataPromise = getSellerFinancials(currentUser.uid, dateRange);
      const data = await Promise.race([dataPromise, timeoutPromise]);
      
      console.log('Financials data received:', data);
      setFinancials(data.data); // Set the actual metrics data, not the wrapper object
      setError(null);
    } catch (err) {
      console.error('Error fetching financials:', err);
      setError('Failed to load financial data');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getDateRangeLabel = (range) => {
    switch (range) {
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case 'all': return 'All Time';
      default: return 'All Time';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <TickSquare set="bold" size={16} primaryColor={COLORS.SUCCESS_GREEN} />;
      case 'in_progress': return <TimeCircle set="bold" size={16} primaryColor={COLORS.WARNING_ORANGE} />;
      default: return <TimeCircle set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `4px solid ${COLORS.BORDER_COLOR}`,
            borderTop: `4px solid ${COLORS.PRIMARY_BROWN}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{
            fontSize: '16px',
            color: COLORS.NEUTRAL_TEXT,
            ...bodyFont
          }}>
            Loading financial data...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: COLORS.ERROR_RED,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ color: 'white', fontSize: '24px' }}>!</span>
          </div>
          <h2 style={{
            fontSize: '20px',
            color: COLORS.NEUTRAL_TEXT,
            margin: '0 0 8px 0',
            ...headingFont
          }}>
            Error Loading Data
          </h2>
          <p style={{
            fontSize: '14px',
            color: COLORS.ACCENT_GRAY,
            margin: '0 0 16px 0',
            ...bodyFont
          }}>
            {error}
          </p>
          <button
            onClick={fetchFinancials}
            style={{
              background: COLORS.PRIMARY_GRADIENT,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              ...bodyFont
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.NEUTRAL_BG }}>
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <h1 style={{ 
              ...headingFont, 
              fontSize: 12, 
              color: COLORS.NEUTRAL_TEXT, 
              margin: 0 
            }}>
              Seller Hub
            </h1>
            
            {/* Navigation Links */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8
            }}>
              {[
                { id: 'dashboard', label: 'Dashboard', path: '/seller-hub' },
                { id: 'products', label: 'Products', path: '/all-products' },
                { id: 'orders', label: 'Orders', path: '/orders' },
                { id: 'messages', label: 'Messages', path: '/messages' },
                { id: 'settings', label: 'Settings', path: `/store-settings/${currentUser?.uid}` }
              ].map(navItem => (
                <button
                  key={navItem.id}
                  onClick={() => navigate(navItem.path)}
                  style={{
                    background: 'transparent',
                    color: navItem.path === '/seller-hub' ? COLORS.PRIMARY_BROWN : COLORS.ACCENT_GRAY,
                    border: 'none',
                    borderRadius: 20,
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ...bodyFont
                  }}
                  onMouseOver={(e) => {
                    if (navItem.path !== '/seller-hub') {
                      e.currentTarget.style.background = COLORS.NEUTRAL_BG;
                      e.currentTarget.style.color = COLORS.NEUTRAL_TEXT;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (navItem.path !== '/seller-hub') {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = COLORS.ACCENT_GRAY;
                    }
                  }}
                >
                  {navItem.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              style={{
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                position: 'relative'
              }}
                        onClick={() => navigate(`/store-preview/${currentUser?.uid}`)}
          aria-label="Go to Store Preview"
            >
              <Show set="regular" size={20} primaryColor={COLORS.NEUTRAL_TEXT} />
            </button>
            <button
              style={{
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                position: 'relative'
              }}
            >
              <Notification set="regular" size={20} primaryColor={COLORS.NEUTRAL_TEXT} />
              <div style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 8,
                height: 8,
                background: COLORS.ERROR_RED,
                borderRadius: '50%'
              }} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ paddingTop: '72px' }}>
        <div style={{ marginLeft: '280px' }} className="md:block hidden">
          {/* Date Range Filter */}
          <div style={{ padding: '20px 20px 0', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              justifyContent: 'center'
            }}>
              <span style={{
                fontSize: '14px',
                color: COLORS.NEUTRAL_TEXT,
                fontWeight: 600,
                ...bodyFont
              }}>
                Time Period:
              </span>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                {['7d', '30d', 'all'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: dateRange === range ? COLORS.PRIMARY_GRADIENT : COLORS.NEUTRAL_WHITE,
                      color: dateRange === range ? 'white' : COLORS.NEUTRAL_TEXT,
                      border: dateRange === range ? 'none' : `1px solid ${COLORS.BORDER_COLOR}`,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {getDateRangeLabel(range)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Financial Metrics Grid */}
          <div style={{ padding: '0 20px 20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {/* Revenue - Completed Orders */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Wallet set="bold" size={16} primaryColor={COLORS.SUCCESS_GREEN} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Revenue (Completed)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.SUCCESS_GREEN,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.revenue?.completed || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  {financials?.orderCounts?.completed || 0} orders
                </div>
              </div>

              {/* Revenue - Orders In Progress */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <TimeCircle set="bold" size={16} primaryColor={COLORS.WARNING_ORANGE} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Revenue (In Progress)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.WARNING_ORANGE,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.revenue?.inProgress || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  {financials?.orderCounts?.inProgress || 0} orders
                </div>
              </div>

              {/* Subtotal - Completed Orders */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.PRIMARY_BROWN} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Subtotal (Completed)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.PRIMARY_BROWN,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.subtotal?.completed || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Product sales only
                </div>
              </div>

              {/* Tax - Completed Orders */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Tax Collected (Completed)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.ACCENT_GRAY,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.tax?.completed || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Sales tax collected
                </div>
              </div>

              {/* Costs - Completed Orders */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.ERROR_RED} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Costs (Completed)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.ERROR_RED,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.costs?.completed || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  {financials?.orderCounts?.completed || 0} orders
                </div>
              </div>

              {/* Costs - Orders In Progress */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    ...bodyFont
                  }}>
                    Costs (In Progress)
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.ACCENT_GRAY,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.costs?.inProgress || 0)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  {financials?.orderCounts?.inProgress || 0} orders
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '24px',
              maxWidth: '900px',
              margin: '0 auto 24px auto'
            }}>
              {/* Total Revenue */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Wallet set="bold" size={16} primaryColor={COLORS.SUCCESS_GREEN} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 600,
                    ...bodyFont
                  }}>
                    Total Revenue
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.SUCCESS_GREEN,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.revenue?.total || 0)}
                </div>
              </div>

              {/* Total Subtotal */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.PRIMARY_BROWN} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 600,
                    ...bodyFont
                  }}>
                    Total Subtotal
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.PRIMARY_BROWN,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.subtotal?.total || 0)}
                </div>
              </div>

              {/* Total Tax */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 600,
                    ...bodyFont
                  }}>
                    Total Tax Collected
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.ACCENT_GRAY,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.tax?.total || 0)}
                </div>
              </div>

              {/* Total Costs */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Document set="bold" size={16} primaryColor={COLORS.ERROR_RED} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 600,
                    ...bodyFont
                  }}>
                    Total Costs
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.ERROR_RED,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.costs?.total || 0)}
                </div>
              </div>

              {/* Net Profit */}
              <div style={{
                background: COLORS.NEUTRAL_WHITE,
                borderRadius: '12px',
                padding: '20px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <TickSquare set="bold" size={16} primaryColor={COLORS.SUCCESS_GREEN} />
                  <span style={{
                    fontSize: '12px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 600,
                    ...bodyFont
                  }}>
                    Net Profit
                  </span>
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: COLORS.SUCCESS_GREEN,
                  ...headingFont
                }}>
                  {formatCurrency(financials?.profit?.total || 0)}
                </div>
              </div>
            </div>

            {/* Cancellation Dashboard Section */}
            <div style={{
              background: COLORS.NEUTRAL_WHITE,
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h2 style={{
                ...headingFont,
                fontSize: '20px',
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                Cancellation & Refund Analysis
              </h2>
              <SellerCancellationDashboard />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
